<template>
  <v-card id="scale-sample-frequencies" min-height="90vh">
    <v-card-title :class="headerClass">
      <span class="headline">{{ $t('scaleSampleFrequencies') }}</span>
      <v-spacer/>
      <BaseDialogActions @refresh="refresh"/>
    </v-card-title>
    <v-card-text class="pt-4" style="scrollbar-gutter: stable">
      <v-row dense align="center" class="mb-2">
        <v-col cols="12" sm="4" md="3">
          <v-text-field
          v-model="search"
          :label="$t('searchTracts')"
          prepend-inner-icon="mdi-magnify"
          clearable
          dense
          outlined
          hide-details/>
        </v-col>
        <v-spacer/>
        <v-col cols="auto" class="body-2" v-if="!loading && filteredTracts.length > 0">
          {{ $t('scaleSampleFrequencySummary', { tracts: filteredTracts.length, contracts: contractCount }) }}
        </v-col>
      </v-row>
      <v-progress-linear v-if="loading" indeterminate color="primary"/>
      <NoData v-else-if="tracts.length === 0">{{ $t('noScaleSampleFrequencyContracts') }}</NoData>
      <NoData v-else-if="filteredTracts.length === 0">{{ $t('noMatchingTracts') }}</NoData>
      <v-expansion-panels v-else multiple v-model="openPanels">
        <v-expansion-panel v-for="tract in filteredTracts" :key="tract.key">
          <v-expansion-panel-header>
            <v-row dense align="center">
              <v-col cols="auto" class="d-flex align-center">
                <Icon
                margin="mr-2"
                :icon="IconHelpers.getIconForObjectType('tract')"
                iconColor="grey darken-1"
                :small="false"
                :tooltipText="$t('tract')"/>
                <span class="subtitle-1 font-weight-bold">{{ tract.name }}</span>
              </v-col>
              <v-spacer/>
              <v-col cols="auto" class="mr-2 subtitle-1">
                {{ $t('scaleSampleFrequencyTractSummary', { destinations: tract.destinations.length, contracts: tract.contractCount }) }}
              </v-col>
            </v-row>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-for="destination in tract.destinations" :key="destination.destinationAccountId" class="mt-3">
              <div class="d-flex align-center mb-1">
                <Icon
                margin="mr-2"
                :icon="IconHelpers.getIconForObjectType('destination')"
                iconColor="grey darken-1"
                :small="false"
                :tooltipText="$t('destination')"/>
                <span class="subtitle-1">{{ destination.name }}</span>
              </div>
              <v-row v-if="tract.tractId != null" dense class="mb-2">
                <v-col
                v-for="product in destination.tractDestinationProducts"
                :key="product.productId"
                cols="12" sm="6" md="4" lg="3">
                  <v-card outlined class="pa-3">
                    <div class="mb-2">{{ product.productName }}</div>
                    <div class="body-1" :class="{ 'grey--text': product.scaleFrequency == null }">{{ formatFrequency(product.scaleFrequency) }}</div>
                  </v-card>
                </v-col>
                <v-col v-if="destination.tractDestinationProducts.length === 0" cols="12" class="body-2 grey--text">
                  {{ $t('noTractDestinationProducts') }}
                </v-col>
              </v-row>
              <v-sheet v-for="contract in destination.contracts" :key="contract.contractId" outlined class="pa-2 mb-2">
                <div class="d-flex align-center mb-1">
                  <Icon
                  margin="mr-1"
                  :icon="IconHelpers.getIconForObjectType('account')"
                  iconColor="grey darken-1"
                  :small="false"
                  :tooltipText="$t('account')"/>
                  <span class="body-2 font-weight-medium mr-3">{{ contract.accountName }}</span>
                  <template v-if="contract.settingName">
                    <Icon
                    margin="mr-1"
                    :icon="IconHelpers.getIconForObjectType('setting')"
                    iconColor="grey darken-1"
                    :small="false"
                    :tooltipText="$t('setting')"/>
                    <span class="body-2 mr-3">{{ contract.settingName }}</span>
                  </template>
                  <Icon
                  margin="ml-4 mr-4"
                  icon="mdi-information-outline"
                  iconColor="primary"
                  :small="false"
                  :elementId="`scale-sample-frequency-contract-${contract.contractId}`"
                  :tooltipText="$t('contractDetails')"
                  @icon-clicked="openContractDetail(contract)"/>
                  <Icon
                  v-if="contract.paused"
                  icon="mdi-pause-circle"
                  iconColor="warning"
                  :tooltipText="$t('paused')"/>
                  <Icon
                  v-if="contract.isDraft"
                  icon="mdi-file"
                  iconColor="grey"
                  :tooltipText="$t('draft')"/>
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th class="text-left">{{ $t('product') }}</th>
                      <th class="text-right">{{ $t('scaleSampleFrequency') }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="product in contract.products" :key="product.productId">
                      <td>
                        <span :class="{ 'grey--text': !product.productIsScalable }">{{ product.productName }}</span>
                      </td>
                      <td class="text-right">
                        <v-chip v-if="!product.productIsScalable" x-small label>{{ $t('notScalable') }}</v-chip>
                        <v-tooltip v-else bottom>
                          <template #activator="{ on }">
                            <span v-on="on">{{ formatFrequency(product.effectiveScaleFrequency) }}</span>
                          </template>
                          <span>{{ sourceDescription(product) }}</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
              </v-sheet>
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-card-text>
    <Dialog
    :stateId="contractDialogId"
    :enforcePersistence="requireEditApproval"
    @close="contractDialogClosed">
      <ContractDetail
      v-if="viewingContract"
      :contractId="focusedContract.contractId"
      @edit-contract="editContract(false, false)"
      @edit-products="editContract(true, false)"
      @edit-activities="editContract(false, true)"
      @review="refresh"/>
      <EditContract
      v-if="editingContract"
      :contractId="focusedContract.contractId"
      :contractMode="contractMode"
      :shouldAddActivity="shouldAddActivity"
      :editProducts="editProducts"
      :companyRequiresEditApproval="companyInfo.requireApprovalForContractModifications"
      :currentDraftMode="focusedContract.isDraft"
      @contract-changed="closeContractDialog"
      @contract-changed-keep-open="refresh"
      @close="closeContractDialog"/>
    </Dialog>
  </v-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { colorClassForContractMode, uniqueDialogId } from '@/utils/componentHelpers'
import { ScaleSampleFrequencySource } from '@/utils/Enumerations.js'
import IconHelpers from '@/utils/IconHelpers'

export default {
  name: 'ScaleSampleFrequencies',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    ContractDetail: () => import('@/components/contract/contract-detail/ContractDetail.vue'),
    Dialog: () => import('@/components/Dialog.vue'),
    EditContract: () => import('@/components/contract/ContractEditor.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue')
  },

  props: {
    contractMode: {
      type: Object,
      required: true
    }
  },

  data: () => ({
    IconHelpers,
    contractProducts: [],
    tractDestinationProducts: [],
    search: '',
    loading: false,
    openPanels: [],
    focusedContract: undefined,
    viewingContract: false,
    editingContract: false,
    editProducts: false,
    shouldAddActivity: false,
    contractDialogId: uniqueDialogId('scale-sample-frequency-contract')
  }),

  computed: {
    ...mapGetters('user', ['companyInfo']),

    headerClass () {
      return colorClassForContractMode(this.contractMode.value)
    },

    tracts () {
      const tractsByKey = new Map()

      for (const row of this.contractProducts) {
        const tractKey = row.tractId ?? 'none'
        if (!tractsByKey.has(tractKey)) {
          tractsByKey.set(tractKey, {
            key: tractKey,
            tractId: row.tractId,
            name: row.tractName ?? this.$t('noTract'),
            destinationsById: new Map()
          })
        }
        const tract = tractsByKey.get(tractKey)

        if (!tract.destinationsById.has(row.destinationAccountId)) {
          tract.destinationsById.set(row.destinationAccountId, {
            destinationAccountId: row.destinationAccountId,
            name: row.destinationAccountName,
            tractDestinationProducts: [],
            contractsById: new Map()
          })
        }
        const destination = tract.destinationsById.get(row.destinationAccountId)

        if (!destination.contractsById.has(row.contractId)) {
          destination.contractsById.set(row.contractId, {
            contractId: row.contractId,
            accountName: row.accountName,
            settingName: row.settingName,
            paused: row.paused,
            isDraft: row.isDraft,
            products: []
          })
        }
        destination.contractsById.get(row.contractId).products.push(row)
      }

      for (const product of this.tractDestinationProducts) {
        const destination = tractsByKey.get(product.tractId)?.destinationsById.get(product.destinationAccountId)
        if (destination && product.productIsScalable) destination.tractDestinationProducts.push(product)
      }

      const byName = (a, b) => a.name.localeCompare(b.name)
      return [...tractsByKey.values()]
        .map(tract => {
          const destinations = [...tract.destinationsById.values()]
            .map(destination => ({
              destinationAccountId: destination.destinationAccountId,
              name: destination.name,
              tractDestinationProducts: destination.tractDestinationProducts.sort((a, b) => a.productName.localeCompare(b.productName)),
              contracts: [...destination.contractsById.values()]
                .map(contract => ({ ...contract, products: contract.products.sort((a, b) => a.sortOrder - b.sortOrder) }))
                .sort((a, b) => a.accountName.localeCompare(b.accountName) || (a.settingName ?? '').localeCompare(b.settingName ?? ''))
            }))
            .sort(byName)
          return {
            key: tract.key,
            tractId: tract.tractId,
            name: tract.name,
            destinations,
            contractCount: destinations.reduce((count, destination) => count + destination.contracts.length, 0)
          }
        })
        .sort((a, b) => {
          if ((a.tractId == null) !== (b.tractId == null)) return a.tractId == null ? 1 : -1
          return byName(a, b)
        })
    },

    filteredTracts () {
      const query = (this.search ?? '').trim().toLowerCase()
      if (!query) return this.tracts
      return this.tracts.filter(tract => tract.name.toLowerCase().includes(query))
    },

    contractCount () {
      return this.filteredTracts.reduce((count, tract) => count + tract.contractCount, 0)
    },

    requireEditApproval () {
      return !!this.companyInfo?.requireApprovalForContractModifications && this.editingContract && !this.focusedContract?.isDraft
    }
  },

  watch: {
    filteredTracts (tracts) {
      this.openPanels = tracts.map((_, index) => index)
    }
  },

  async created () {
    await this.refresh()
  },

  methods: {
    ...mapActions('contract', ['fetchScaleSampleFrequencies']),
    ...mapActions('dialog', ['openOrUpdateDialog', 'closeDialogsAtOrAbove']),

    async refresh () {
      this.loading = true
      try {
        const { contractProducts, tractDestinationProducts } = await this.fetchScaleSampleFrequencies()
        this.contractProducts = contractProducts
        this.tractDestinationProducts = tractDestinationProducts
      } finally {
        this.loading = false
      }
    },

    sourceDescription (product) {
      return product.source === ScaleSampleFrequencySource.None.value
        ? this.$t('scaleSampleFrequencyNotDefinedDescription')
        : ScaleSampleFrequencySource.fromInt(product.source)
    },

    formatFrequency (frequency) {
      if (frequency == null) return this.$t('notApplicable')
      const percent = parseFloat(frequency)
      return percent > 0 ? `1:${Math.round(100 / percent)}` : '1:1'
    },

    openContractDetail (contract) {
      this.focusedContract = contract
      this.editingContract = false
      this.viewingContract = true
      this.openOrUpdateDialog({ id: this.contractDialogId, width: '80vw' })
    },

    editContract (editProducts, shouldAddActivity) {
      this.editProducts = editProducts
      this.shouldAddActivity = shouldAddActivity
      this.viewingContract = false
      this.editingContract = true
      this.openOrUpdateDialog({ id: this.contractDialogId, width: '80vw' })
    },

    closeContractDialog () {
      this.closeDialogsAtOrAbove(this.contractDialogId)
    },

    async contractDialogClosed () {
      this.viewingContract = false
      this.editingContract = false
      this.editProducts = false
      this.shouldAddActivity = false
      this.focusedContract = undefined
      await this.refresh()
    }
  }
}
</script>
